/* OpenAI Ads: consent-gated receipt measurement through the documented image
 * endpoint. No third-party SDK or automatic advanced matching is loaded.
 * Only confirmed Shopify receipt events are observed; submission is untouched.
 * Contract: https://learn.chatgpt.com/ads/image-tag (checked 2026-10-06).
 */
(() => {
  'use strict';

  const GLOBAL_KEY = 'ZinVoltOpenAIAds';
  if (window[GLOBAL_KEY]) return;
  const PIXEL_ID = 'HMNqnuFywSAEhQkvtXfpKn';
  const COOKIE_NAME = '__zv_oai_oppref';
  const MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
  const ENDPOINT = 'https://bzr.openai.com/v1/sdk/events';
  const LEAD_FORMS = new Set(['1101817', '1101824', '1101834']);
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const OPPREF = /^[A-Za-z0-9._~:+/=-]{1,1024}$/;
  const seenReceipts = new Set();
  const seenForms = new WeakSet();
  const pendingImages = new Set();
  let memoryAttribution = null;
  let revokedOnThisPage = false;
  let hadGrantedConsent = false;
  let attempted = 0;

  const isProduction = () => {
    const url = new URL(window.location.href);
    return url.origin === 'https://zinvolt.com'
      && String(window.Shopify?.theme?.role || '').toLowerCase() === 'main'
      && window.Shopify?.designMode !== true
      && !document.documentElement?.classList?.contains('shopify-design-mode')
      && !['preview_theme_id', 'design_theme_id', 'pb'].some(key => url.searchParams.has(key));
  };

  const hasConsent = () => {
    try {
      const consent = window.ZinVoltLeadAttribution?.consentSnapshot?.();
      return consent?.source === 'zinvolt_cmp'
        && consent.pending_sync === false
        && consent.ad_storage === 'granted'
        && consent.ad_user_data === 'granted';
    } catch (_) { return false; }
  };

  const clearAttribution = () => {
    memoryAttribution = null;
    try {
      document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; Secure; SameSite=Lax`;
    } catch (_) { /* Storage denial must never affect a form. */ }
  };

  const validAttribution = record => record && typeof record.value === 'string'
    && OPPREF.test(record.value)
    && Number.isFinite(record.expiresAt)
    && record.expiresAt > Date.now()
    && record.expiresAt <= Date.now() + MAX_AGE_SECONDS * 1000;

  const readAttribution = () => {
    if (!hasConsent()) return '';
    if (validAttribution(memoryAttribution)) return memoryAttribution.value;
    try {
      const entry = document.cookie.split(';').map(part => part.trim())
        .find(part => part.startsWith(`${COOKIE_NAME}=`));
      if (!entry) return '';
      const record = JSON.parse(decodeURIComponent(entry.slice(COOKIE_NAME.length + 1)));
      if (validAttribution(record)) {
        memoryAttribution = record;
        return record.value;
      }
    } catch (_) { /* Malformed or inaccessible storage is ignored. */ }
    clearAttribution();
    return '';
  };

  const captureAttribution = () => {
    if (!isProduction() || !hasConsent() || revokedOnThisPage) return;
    const params = new URL(window.location.href).searchParams;
    const primary = params.getAll('oppref');
    const fallback = params.getAll('zv_oa_click_id');
    if (primary.length > 1 || fallback.length > 1) return;
    if (primary.length && fallback.length && primary[0] !== fallback[0]) return;
    const value = primary.length ? primary[0] : fallback[0];
    if (!value || value === 'preview_mock' || !OPPREF.test(value)) return;
    // Capture only this documented ad-click parameter, never arbitrary query
    // strings, referrers, email, contact fields or the CRM's lead identifier.
    memoryAttribution = { value, expiresAt: Date.now() + MAX_AGE_SECONDS * 1000 };
    try {
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(memoryAttribution))}; Max-Age=${MAX_AGE_SECONDS}; Path=/; Secure; SameSite=Lax`;
    } catch (_) { /* Same-page attribution can work without cookie storage. */ }
  };

  // Private cart attributes are copied onto the eventual Shopify order.
  // Only the authenticated paid-order handler emits a purchase conversion.
  const CART_ATTRIBUTES = Object.freeze({
    consent: 'zv_oa_consent__',
    click: 'zv_oa_oppref__',
    expiry: 'zv_oa_oppref_expires_at__'
  });
  let cartSyncPromise = null;
  let queuedCartContext = null;
  let lastCartContext = '';

  const readCartContext = () => {
    if (!isProduction() || typeof window.fetch !== 'function') return null;
    const context = window.ZinVoltLeadAttribution?.openAICartContext?.();
    const consent = context?.consent_snapshot;
    if (consent?.source !== 'zinvolt_cmp') return null;
    // Write the current denied/pending snapshot too, so an old grant cannot
    // survive a withdrawal on an existing cart. Never send customer fields.
    const allowed = hasConsent();
    return JSON.stringify({ attributes: {
      [CART_ATTRIBUTES.consent]: JSON.stringify(consent),
      [CART_ATTRIBUTES.click]: allowed ? (context.oppref || '') : '',
      [CART_ATTRIBUTES.expiry]: allowed ? (context.oppref_expires_at || '') : ''
    } });
  };

  const queueCartContext = () => {
    try {
      const context = readCartContext();
      if (!context) return Promise.resolve(false);
      queuedCartContext = context;
      if (cartSyncPromise) return cartSyncPromise;
      cartSyncPromise = (async () => {
        let saved = true;
        while (queuedCartContext) {
          const payload = queuedCartContext;
          queuedCartContext = null;
          if (payload === lastCartContext) continue;
          try {
            // Locale-aware same-origin API; only our three attributes change.
            const route = String(window.Shopify?.routes?.root || '/');
            const url = new URL(`${route.replace(/\/?$/, '/')}cart/update.js`, window.location.origin);
            if (url.origin !== window.location.origin) { saved = false; continue; }
            const response = await window.fetch(url.href, {
              method: 'POST', credentials: 'same-origin', keepalive: true,
              headers: { 'Content-Type': 'application/json' }, body: payload
            });
            if (response.ok) lastCartContext = payload;
            else saved = false;
          } catch (_) { saved = false; }
        }
        return saved;
      })().finally(() => { cartSyncPromise = null; });
      return cartSyncPromise;
    } catch (_) { return Promise.resolve(false); }
  };

  const flushCartContext = (timeoutMs = 800) => {
    // A slow attribution write may never stop or materially delay checkout.
    // The request continues after this bounded wait; no order is created here.
    const wait = Math.min(1500, Math.max(0, Number(timeoutMs) || 800));
    return Promise.race([
      queueCartContext(),
      new Promise(resolve => window.setTimeout(() => resolve(false), wait))
    ]).catch(() => false);
  };

  const updateConsent = () => {
    try {
      if (!isProduction()) return;
      const consent = window.ZinVoltLeadAttribution?.consentSnapshot?.();
      // A granted local choice awaiting Shopify synchronization is not a
      // withdrawal. Keep previously consented attribution untouched while
      // hasConsent() continues to block all new storage and measurement.
      if (consent?.source === 'zinvolt_cmp' && consent.pending_sync === true
        && consent.ad_storage === 'granted' && consent.ad_user_data === 'granted') { queueCartContext(); return; }
      if (hasConsent()) {
        hadGrantedConsent = true;
        captureAttribution();
      }
      else if (window.zvConsentState) {
        if (hadGrantedConsent) revokedOnThisPage = true;
        clearAttribution();
      }
      queueCartContext();
    } catch (_) { clearAttribution(); }
  };

  const claimReceipt = (detail, formId, form) => {
    // Identity is used only in memory for deduplication. It is never sent in a
    // URL. Every outgoing event receives an unrelated random event UUID.
    const submissionUuid = String(detail.submissionUuid || '');
    const eventId = String(detail.eventId || '');
    const key = UUID.test(submissionUuid) ? `${formId}:${submissionUuid}`
      : new RegExp(`^zv-${formId}-[0-9a-f-]{36}$`, 'i').test(eventId) ? eventId : '';
    if (key) {
      if (seenReceipts.has(key)) return false;
      seenReceipts.add(key);
      return true;
    }
    if (!form || typeof form !== 'object' || seenForms.has(form)) return false;
    seenForms.add(form);
    return true;
  };

  const sendReceipt = (detail, formId, form, eventName, customName = '') => {
    try {
      // Claim denied events too: granting consent must not replay earlier
      // receipts when an unrelated observer emits the same success again.
      if (!isProduction() || !claimReceipt(detail, formId, form) || !hasConsent()) return;
      captureAttribution();
      const persisted = eventName === 'lead_created' && detail.openai?.schema_version === '1.0' ? detail.openai : null;
      const persistedId = String(persisted?.event_id || '');
      const persistedUuid = persistedId.startsWith('evt_') ? persistedId.slice(4) : '';
      const hasPersistedId = UUID.test(persistedUuid) && persistedUuid !== String(detail.submissionUuid || '')
        && persisted.submission_uuid === String(detail.submissionUuid || '');
      const eventUuid = hasPersistedId ? persistedUuid : window.crypto?.randomUUID?.();
      if (!eventUuid || !UUID.test(eventUuid)) return;
      const url = new URL(ENDPOINT);
      url.searchParams.set('pid', PIXEL_ID);
      url.searchParams.set('event', eventName);
      url.searchParams.set('event_id', `evt_${eventUuid}`);
      url.searchParams.set('data[type]', customName ? 'custom' : 'customer_action');
      if (customName) url.searchParams.set('custom_event_name', customName);
      // Match the stored receipt envelope exactly, including an intentionally
      // empty click. This avoids browser/server attribution disagreement.
      const persistedExpiry = Date.parse(persisted?.oppref_expires_at || '');
      const persistedOppref = typeof persisted?.oppref === 'string' && OPPREF.test(persisted.oppref)
        && persistedExpiry > Date.now() && persistedExpiry <= Date.now() + MAX_AGE_SECONDS * 1000
        ? persisted.oppref : '';
      const oppref = hasPersistedId ? persistedOppref : readAttribution();
      if (oppref) url.searchParams.set('oppref', oppref);
      if (!hasConsent()) return;
      const image = new Image(1, 1);
      image.referrerPolicy = 'origin';
      image.alt = '';
      const release = () => pendingImages.delete(image);
      image.onload = release;
      image.onerror = release;
      pendingImages.add(image);
      image.src = url.toString();
      attempted += 1;
      window.setTimeout(release, 15000);
    } catch (_) { /* Receipt tracking must never reject or repeat a form. */ }
  };

  const onBridgeSuccess = event => {
    try {
      const detail = event?.detail || {};
      const formId = String(detail.formId || '');
      const form = detail.form;
      if (form?.tagName !== 'FORM' || String(form.dataset?.zvFormId || '') !== formId) return;
      if (LEAD_FORMS.has(formId)) sendReceipt(detail, formId, form, 'lead_created');
      else if (formId === '1101850') sendReceipt(detail, formId, form, 'custom', 'newsletter_signup');
    } catch (_) { /* Never alter the existing success UI. */ }
  };

  const onNativeContactSuccess = event => {
    try {
      const detail = event?.detail || {};
      if (String(detail.formId || '') !== '1105194' || !detail.form) return;
      sendReceipt(detail, '1105194', detail.form, 'custom', 'contact_message_sent');
    } catch (_) { /* No customer message is touched. */ }
  };

  window[GLOBAL_KEY] = Object.freeze({
    version: '2026-10-13.1',
    transport: 'image',
    flushCartContext,
    get attemptedEvents() { return attempted; }
  });
  document.addEventListener('zv:shopify-form-success', onBridgeSuccess);
  document.addEventListener('zv:native-contact-success', onNativeContactSuccess);
  document.addEventListener('zv:cookie-consent-updated', updateConsent);
  window.addEventListener('pageshow', updateConsent);
  updateConsent();
})();
